import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, X, SlidersHorizontal, RotateCcw } from "lucide-react";
import { useStoreSettings } from "../hooks/useHomeContent";
import { useCatalog } from "../context/CatalogContext";
import { genderOf } from "../lib/gender";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import DesignCard from "../components/DesignCard";
import useReveal from "../hooks/useReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GENDER_CHIPS = [
  { slug: "all", label: "Everyone" },
  { slug: "ladies", label: "Ladies" },
  { slug: "men", label: "Men" },
  { slug: "unisex", label: "Unisex" },
  { slug: "kids", label: "Kids" },
];

// Absolute-max weight cap for the slider — beyond this, sliders become unwieldy.
const ABS_MAX = 500;

export default function CategoryPage() {
  const { slug, sub } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getCategoryMerged, getDesignsMerged, hideDefaults, homeItems } = useCatalog();
  const store = useStoreSettings();
  const cat = getCategoryMerged(slug);

  const [activeSub, setActiveSub] = useState(null);
  const [query, setQuery] = useState("");
  const [gender, setGender] = useState(searchParams.get("gender") || "all");
  const [purity, setPurity] = useState(searchParams.get("purity") || "all");
  const [minW, setMinW] = useState(Number(searchParams.get("min")) || 0);
  const [maxW, setMaxW] = useState(Number(searchParams.get("max")) || 0); // 0 = "no cap"
  const [showFilters, setShowFilters] = useState(false);

  // Keep filter state in the URL so links can be shared and reloads persist.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (gender && gender !== "all") next.set("gender", gender); else next.delete("gender");
    if (purity && purity !== "all") next.set("purity", purity); else next.delete("purity");
    if (minW > 0) next.set("min", String(minW)); else next.delete("min");
    if (maxW > 0) next.set("max", String(maxW)); else next.delete("max");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, purity, minW, maxW]);

  // Keep the URL and the active tab in sync. When someone opens
  // /collections/gold/bangles we honour that; otherwise default to the first sub.
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!cat || !cat.subCategories.length) return;
    const first = cat.subCategories[0].slug;
    const match = sub
      ? cat.subCategories.find((s) => s.slug === sub)
      : null;
    const next = match ? match.slug : first;
    setActiveSub(next);
    if (!sub) {
      navigate(`/collections/${cat.slug}/${next}`, { replace: true });
    }
  }, [slug, sub, cat, navigate]);

  useReveal();

  const designs = useMemo(() => {
    if (!cat || !activeSub) return [];
    return getDesignsMerged(cat.slug, activeSub);
  }, [cat, activeSub, getDesignsMerged]);

  // Purities available in *this* sub-category — computed from live data so
  // gold shows 22k/18k/24k, diamond shows VS1/VVS2, silver shows 925 Sterling, etc.
  const purityOptions = useMemo(() => {
    const seen = new Set();
    designs.forEach((d) => { if (d.purity) seen.add(d.purity); });
    return Array.from(seen).sort();
  }, [designs]);

  // Smart weight cap for the slider — based on the heaviest design in this sub.
  const weightCap = useMemo(() => {
    if (!designs.length) return 50;
    const max = designs.reduce((m, d) => Math.max(m, Number(d.weight) || 0), 0);
    return Math.min(ABS_MAX, Math.ceil(max / 5) * 5) || 50;
  }, [designs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return designs.filter((d) => {
      if (gender !== "all" && genderOf(d) !== gender) return false;
      if (purity !== "all" && d.purity !== purity) return false;
      const w = Number(d.weight) || 0;
      if (minW > 0 && w < minW) return false;
      if (maxW > 0 && w > maxW) return false;
      if (!q) return true;
      return (
        d.name.toLowerCase().includes(q) ||
        d.styleName.toLowerCase().includes(q) ||
        d.number.includes(q)
      );
    });
  }, [designs, query, gender, purity, minW, maxW]);

  // A single tally of how many filters are actively in use — drives the
  // little "3" badge on the mobile toggle and the disabled state of Reset.
  const activeFilterCount =
    (gender !== "all" ? 1 : 0) +
    (purity !== "all" ? 1 : 0) +
    (minW > 0 ? 1 : 0) +
    (maxW > 0 ? 1 : 0);

  const resetFilters = () => {
    setGender("all");
    setPurity("all");
    setMinW(0);
    setMaxW(0);
    setQuery("");
  };

  if (!cat) {
    return (
      <div className="alankar-site">
        <Header />
        <div className="wrap" style={{ padding: "140px 28px", textAlign: "center" }}>
          <h2>Category not found</h2>
          <p style={{ marginTop: 12 }}>
            <button className="btn btn-outline" onClick={() => navigate("/")}>
              <ArrowLeft size={16} /> Back to Home
            </button>
          </p>
        </div>
        <Footer />
        <FloatingWhatsApp />
      </div>
    );
  }

  const activeSubObj = cat.subCategories.find((s) => s.slug === activeSub);

  return (
    <div className="alankar-site">
      <Header />

      <section className="cat-hero">
        <div
          className="cat-hero-bg"
          style={{ backgroundImage: `url(${cat.cover})` }}
        />
        <div className="cat-hero-overlay" />
        <div className="wrap cat-hero-inner">
          <nav className="cat-crumbs">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/#collections">Collections</Link>
            <span>/</span>
            <Link to={`/collections/${cat.slug}`}>{cat.label}</Link>
            {activeSubObj ? (
              <>
                <span>/</span>
                <span data-testid="crumb-sub">{activeSubObj.label}</span>
              </>
            ) : null}
          </nav>
          <span className="eyebrow" style={{ color: "#F3D9A8" }}>
            {cat.subCategories.length} categories
            {hideDefaults ? "" : ` · ${cat.subCategories.length * 54}+ designs`}
          </span>
          <h1>
            {cat.label} <span className="shine">Collection</span>
          </h1>
          <p>{cat.tagline}</p>
        </div>
      </section>

      <section className="cat-body">
        <div className="wrap">
          <div className="cat-tabs" role="tablist">
            {cat.subCategories.map((s) => {
              const count = getDesignsMerged(cat.slug, s.slug).length;
              return (
                <button
                  key={s.slug}
                  type="button"
                  role="tab"
                  className={`cat-tab ${activeSub === s.slug ? "on" : ""}`}
                  onClick={() => {
                    setActiveSub(s.slug);
                    setQuery("");
                    // Sync the URL so refreshing / sharing lands on the same tab.
                    navigate(`/collections/${cat.slug}/${s.slug}`);
                  }}
                >
                  {s.label}
                  <span className="cat-tab-count">{count}</span>
                </button>
              );
            })}
          </div>

          {/* Mobile-friendly toggle. On desktop the panel is always open. */}
          <div className="cat-filter-toggle" data-testid="cat-filter-toggle-wrap">
            <button
              type="button"
              className={`btn ${showFilters ? "btn-primary" : "btn-outline"}`}
              onClick={() => setShowFilters((v) => !v)}
              data-testid="cat-filter-toggle"
            >
              <SlidersHorizontal size={16} />
              <span style={{ marginLeft: 6 }}>
                Filters{activeFilterCount ? ` · ${activeFilterCount}` : ""}
              </span>
            </button>
            {activeFilterCount > 0 && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={resetFilters}
                data-testid="cat-filter-reset-inline"
                style={{ marginLeft: 8 }}
              >
                <RotateCcw size={14} />
                <span style={{ marginLeft: 6 }}>Reset</span>
              </button>
            )}
          </div>

          <div
            className={`cat-filters ${showFilters ? "open" : ""}`}
            data-testid="cat-filters"
          >
            {/* Gender chips */}
            <div className="cat-filter-row" data-testid="cat-gender-row">
              <span className="cat-filter-label">For</span>
              <div className="cat-chip-group">
                {GENDER_CHIPS.map((g) => (
                  <button
                    key={g.slug}
                    type="button"
                    className={`cat-chip ${gender === g.slug ? "on" : ""}`}
                    onClick={() => setGender(g.slug)}
                    data-testid={`cat-gender-${g.slug}`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Purity chips (dynamic — from actual design data of this sub) */}
            {purityOptions.length > 1 && (
              <div className="cat-filter-row" data-testid="cat-purity-row">
                <span className="cat-filter-label">Purity</span>
                <div className="cat-chip-group">
                  <button
                    type="button"
                    className={`cat-chip ${purity === "all" ? "on" : ""}`}
                    onClick={() => setPurity("all")}
                    data-testid="cat-purity-all"
                  >
                    All
                  </button>
                  {purityOptions.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`cat-chip ${purity === p ? "on" : ""}`}
                      onClick={() => setPurity(p)}
                      data-testid={`cat-purity-${p.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Weight range */}
            <div className="cat-filter-row" data-testid="cat-weight-row">
              <span className="cat-filter-label">Weight</span>
              <div className="cat-weight">
                <span className="cat-weight-badge">
                  {minW > 0 ? minW : 0} g – {maxW > 0 ? `${maxW} g` : `${weightCap} g (max)`}
                </span>
                <label>
                  <span className="cat-weight-hint">Min</span>
                  <input
                    type="range"
                    min={0}
                    max={weightCap}
                    step={1}
                    value={minW}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setMinW(v);
                      if (maxW > 0 && v > maxW) setMaxW(v);
                    }}
                    data-testid="cat-weight-min"
                  />
                </label>
                <label>
                  <span className="cat-weight-hint">Max</span>
                  <input
                    type="range"
                    min={0}
                    max={weightCap}
                    step={1}
                    value={maxW > 0 ? maxW : weightCap}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setMaxW(v === weightCap ? 0 : v); // top = "no cap"
                      if (minW > 0 && v < minW) setMinW(v);
                    }}
                    data-testid="cat-weight-max"
                  />
                </label>
              </div>
            </div>

            <div className="cat-filter-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={resetFilters}
                disabled={activeFilterCount === 0}
                data-testid="cat-filter-reset"
              >
                <RotateCcw size={14} />
                <span style={{ marginLeft: 6 }}>Clear filters</span>
              </button>
            </div>
          </div>

          <div className="cat-toolbar">
            <div className="cat-title">
              <h2>{activeSubObj ? activeSubObj.label : ""}</h2>
              <p data-testid="cat-count">{filtered.length} designs shown</p>
            </div>
            <div className="cat-search">
              <Search size={16} />
              <input
                type="text"
                placeholder={`Search in ${activeSubObj ? activeSubObj.label : ""}…`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} aria-label="Clear">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="design-grid">
            {filtered.map((d) => (
              <DesignCard key={d.id} design={d} />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="cat-empty">
              <p>
                No designs matched your filters
                {query ? ` for “${query}”` : ""}. Try widening the weight range or
                clearing a filter.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={resetFilters}
                  data-testid="cat-empty-reset"
                >
                  <RotateCcw size={14} />
                  <span style={{ marginLeft: 6 }}>Clear filters</span>
                </button>
                <a className="btn btn-outline" target="_top" rel="noreferrer" href={store.waGeneric}>
                  Ask on WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
